import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@/components/atoms/Tooltip";
import { PropertyThumbnailImage } from "./PropertyThumbnailImage";
import { Bed, Bath, CalendarDays, FileText, Image as ImageIcon } from "lucide-react";
import { AvailabilityDot } from "@/components/atoms/AvailabilityDot";
import { Badge } from "@/components/atoms/Badge";
import { Input } from "@/components/atoms/Input";
import { ColumnFilter, ColumnFilterField } from "@/components/molecules/ColumnFilter";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { SyncStatus } from "@/components/molecules/SyncStatus";
import { DataTable, type Column, type RowSelection } from "@/components/organisms/DataTable";
import { cn } from "@/lib/cn";
import { formatPrice, formatRelativeTime } from "@/lib/format";
import { propertyListSubtitle } from "@/lib/propertyDisplay";
import type { SortDirection } from "@/types/api";
import type { AvailabilityColor, PropertyFilter, PropertySummary } from "@/types/entities";

interface Props {
  rows: PropertySummary[];
  loading: boolean;
  selectedKey: string | null;
  onSelect: (row: PropertySummary) => void;
  onAvailability?: (row: PropertySummary) => void;
  sort?: { field: string; direction: SortDirection };
  onSortChange: (field: string, direction: SortDirection) => void;
  imagePresenceByPropertyId?: Record<string, boolean>;
  imageUrlsByPropertyId?: Record<string, string[]>;
  fallbackLogoUrlByPropertyId?: Record<string, string>;
  imagePresenceLoading?: boolean;
  returnView?: "list" | "tile";
  /** Refetches the list after a row's resync is queued, so the status updates. */
  onResynced?: () => void;
  /** Current filter, so column headers can show what they are narrowing by. */
  filter: PropertyFilter;
  onFilterChange: (next: Partial<PropertyFilter>) => void;
  /** Omit for a plain table; supply to grow the leading checkbox column. */
  selection?: RowSelection<PropertySummary>;
}

const SYNC_STATUS_OPTIONS = [
  { value: "", label: "Any sync status" },
  { value: "COMPLETED", label: "In Sync" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "FAILED", label: "Failed" },
  { value: "CHECKPOINT", label: "Checkpoint" },
];

const AVAILABILITY_OPTIONS = [
  { value: "", label: "Any availability" },
  { value: "RED", label: "No availability", leading: <AvailabilityDot color="RED" size={12} /> },
  { value: "BLUE", label: "Manual availability", leading: <AvailabilityDot color="BLUE" size={12} /> },
  { value: "BLACK", label: "Default availability", leading: <AvailabilityDot color="BLACK" size={12} /> },
];

const TONE_BY_STATUS: Record<string, "success" | "warning" | "danger" | "info" | "neutral"> = {
  Available: "success",
  "Under Offer": "warning",
  "Under offer": "warning",
  "Let agreed": "info",
  Sold: "danger",
  Withdrawn: "neutral",
};

function PropertyListImage({
  urls,
  fallbackLogoUrl,
  alt,
}: {
  urls: string[] | undefined;
  fallbackLogoUrl: string;
  alt: string;
}) {
  return (
    <PropertyThumbnailImage
      urls={urls}
      fallbackLogoUrl={fallbackLogoUrl}
      alt={alt}
      fallbackLogoSize={22}
    />
  );
}

export function PropertiesTable({
  rows,
  loading,
  selectedKey,
  onSelect,
  onAvailability,
  sort,
  onSortChange,
  imagePresenceByPropertyId = {},
  imageUrlsByPropertyId = {},
  fallbackLogoUrlByPropertyId = {},
  imagePresenceLoading = false,
  returnView = "list",
  onResynced,
  filter,
  onFilterChange,
  selection,
}: Props) {
  const navigate = useNavigate();

  const columns = useMemo<Column<PropertySummary>[]>(
    () => [
      {
        id: "displayAddress",
        header: "Property",
        sortable: true,
        width: "w-[240px]",
        cellTooltip: (r) =>
          [r.fullAddress ?? r.displayAddress ?? "(no address)", r.vendorName ?? propertyListSubtitle(r)]
            .filter(Boolean)
            .join("\n"),
        cell: (r) => (
          <div className="min-w-0 flex items-center gap-2">
            <div className="h-10 w-12 shrink-0 overflow-hidden rounded-md border border-bridge-border/70 bg-bridge-surface">
              <PropertyListImage
                urls={imageUrlsByPropertyId[r.id]}
                fallbackLogoUrl={fallbackLogoUrlByPropertyId[r.id] ?? ""}
                alt={r.fullAddress ?? r.displayAddress ?? "Property"}
              />
            </div>
            <div className="min-w-0 leading-tight">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-[13px] font-semibold text-slate-800">{r.fullAddress ?? r.displayAddress ?? "(no address)"}</span>
              </div>
              <div className="truncate text-[11px] font-semibold uppercase tracking-wide text-bridge-muted">
                {r.vendorName ?? propertyListSubtitle(r)}
              </div>
            </div>
            {r.crmSyncStatus === "IN_PROGRESS" && (
               <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                 <span className="relative flex h-1.5 w-1.5">
                   <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                   <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500"></span>
                 </span>
                 Syncing
               </span>
            )}
          </div>
        ),
      },
      {
        id: "apiDisplayAddress",
        header: "Display Address",
        width: "w-[360px]",
        // DataTable derives the offset from the live widths of preceding columns.
        stickyClassName: "sticky",
        cellTooltip: (r) => r.apiDisplayAddress ?? "",
        cell: (r) =>
          r.apiDisplayAddress ? (
            <span className="block truncate text-xs font-medium text-slate-700">{r.apiDisplayAddress}</span>
          ) : (
            <span className="text-bridge-subtle">—</span>
          ),
      },
      {
        id: "propertyType",
        header: "Type",
        sortable: true,
        width: "w-[150px]",
        cell: (r) => (
          <span className="text-xs font-medium text-slate-700">
            {r.propertyType ?? <span className="text-bridge-subtle">—</span>}
          </span>
        ),
      },
      {
        id: "availabilityStatus",
        header: "Status",
        sortable: true,
        width: "w-[130px]",
        cell: (r) =>
          r.availabilityStatus ? (
            <Badge tone={TONE_BY_STATUS[r.availabilityStatus] ?? "neutral"} className="h-5 rounded-full px-2 text-[10px]">
              {r.availabilityStatus}
            </Badge>
          ) : (
            <span className="text-bridge-subtle">—</span>
          ),
      },
      {
        id: "bedrooms",
        header: "Beds / Baths",
        sortable: true,
        width: "w-[170px]",
        headerAccessory: (
          <ColumnFilter
            label="Bedrooms"
            tooltip="Show only listings with at least this many bedrooms."
            active={filter.minBedrooms != null}
            onClear={() => onFilterChange({ minBedrooms: undefined })}
          >
            <ColumnFilterField label="Minimum">
              <Input
                type="number"
                placeholder="Min beds"
                value={filter.minBedrooms ?? ""}
                onChange={(e) => onFilterChange({ minBedrooms: e.target.value ? Number(e.target.value) : undefined })}
              />
            </ColumnFilterField>
          </ColumnFilter>
        ),
        cell: (r) => (
          <div className="flex items-center gap-2.5 text-xs text-slate-700">
            <span className="inline-flex items-center gap-1">
              <Bed size={11} /> {r.bedrooms ?? "—"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Bath size={11} /> {r.bathrooms ?? "—"}
            </span>
          </div>
        ),
      },
      {
        id: "price",
        header: "Price",
        sortable: true,
        width: "w-[160px]",
        headerAccessory: (
          <ColumnFilter
            label="Price"
            tooltip="Hide listings priced outside this range."
            active={filter.minPrice != null || filter.maxPrice != null}
            onClear={() => onFilterChange({ minPrice: undefined, maxPrice: undefined })}
          >
            <ColumnFilterField label="Minimum">
              <Input
                type="number"
                placeholder="Min price"
                value={filter.minPrice ?? ""}
                onChange={(e) => onFilterChange({ minPrice: e.target.value ? Number(e.target.value) : undefined })}
              />
            </ColumnFilterField>
            <ColumnFilterField label="Maximum">
              <Input
                type="number"
                placeholder="Max price"
                value={filter.maxPrice ?? ""}
                onChange={(e) => onFilterChange({ maxPrice: e.target.value ? Number(e.target.value) : undefined })}
              />
            </ColumnFilterField>
          </ColumnFilter>
        ),
        cell: (r) => <span className="text-xs font-semibold text-slate-800">{formatPrice(r.price, r.currency)}</span>,
      },
      {
        id: "availability",
        header: "Availability",
        width: "w-[160px]",
        headerAccessory: (
          <ColumnFilter
            label="Availability"
            tooltip="Show listings by their availability status."
            active={Boolean(filter.availabilityColor)}
            onClear={() => onFilterChange({ availabilityColor: undefined })}
          >
            <MenuSelect
              hideLabel
              value={filter.availabilityColor ?? ""}
              onChange={(v) => onFilterChange({ availabilityColor: (v || undefined) as AvailabilityColor | undefined })}
              options={AVAILABILITY_OPTIONS}
              placeholder="Any availability"
            />
          </ColumnFilter>
        ),
        // A lone dot has no left edge to line up with the labels above it, so
        // it stays centred in its column rather than floating at the margin.
        cell: (r) => (
          <div className="flex items-center justify-center">
            <AvailabilityDot color={r.availabilityColor} size={14} />
          </div>
        ),
      },
      {
        id: "atlasModifiedAt",
        header: "Updated",
        sortable: true,
        width: "w-[130px]",
        cell: (r) => <span className="text-xs font-medium text-bridge-muted">{formatRelativeTime(r.atlasModifiedAt)}</span>,
      },
      {
        id: "syncStatus",
        header: "Sync",
        sortable: false,
        width: "w-[200px]",
        headerAccessory: (
          <ColumnFilter
            label="Sync"
            tooltip="Show listings by their CRM sync state."
            active={Boolean(filter.crmSyncStatus)}
            onClear={() => onFilterChange({ crmSyncStatus: undefined })}
          >
            <MenuSelect
              hideLabel
              value={filter.crmSyncStatus ?? ""}
              onChange={(v) => onFilterChange({ crmSyncStatus: v || undefined })}
              options={SYNC_STATUS_OPTIONS}
              placeholder="Any sync status"
            />
          </ColumnFilter>
        ),
        cell: (r) => (
          <SyncStatus
            crmSyncStatus={r.crmSyncStatus || r.syncAlto}
            entityType="PROPERTY"
            entityId={r.id}
            align="start"
            compact
            onResynced={onResynced}
          />
        ),
      },
      {
        id: "actions",
        header: "",
        align: "right",
        width: "w-[140px]",
        cell: (r) => (
          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {onAvailability && (
              <Tooltip content="Availability" side="top">
                <button
                  type="button"
                  onClick={() => onAvailability(r)}
                  className="p-1 rounded-md hover:bg-bridge-surface/80 text-bridge-muted hover:text-bridge-accent transition-colors focus-ring"
                  aria-label="Availability"
                >
                  <CalendarDays size={14} />
                </button>
              </Tooltip>
            )}
            <Tooltip content="View documents" side="top">
              <button
                type="button"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (r.externalId) params.set("externalPropertyId", r.externalId);
                  params.set("returnView", returnView);
                  navigate(`/documents?${params.toString()}`);
                }}
                className="p-1 rounded-md hover:bg-bridge-surface/80 text-bridge-muted hover:text-bridge-accent transition-colors focus-ring"
                aria-label="View documents"
              >
                <FileText size={14} />
              </button>
            </Tooltip>
            {(() => {
              const hasImages = imagePresenceByPropertyId[r.id];
              const imageStateKnown = r.id in imagePresenceByPropertyId;
              const imageTooltip = imageStateKnown
                ? hasImages
                  ? "View images"
                  : "No images"
                : "View images";

              return (
                <Tooltip content={imageTooltip} side="top">
                  <button
                    type="button"
                    onClick={() => {
                      const params = new URLSearchParams({ propertyId: r.id });
                      if (r.externalId) params.set("externalPropertyId", r.externalId);
                      params.set("returnView", returnView);
                      navigate(`/images?${params.toString()}`);
                    }}
                    className={cn(
                      "p-1 rounded-md transition-colors focus-ring",
                      !imageStateKnown || imagePresenceLoading
                        ? "text-bridge-muted hover:bg-bridge-surface/80 hover:text-bridge-accent"
                        : hasImages
                          ? "text-green-600 hover:bg-green-50 hover:text-green-700"
                          : "text-red-600 hover:bg-red-50 hover:text-red-700",
                    )}
                    aria-label={imageTooltip}
                  >
                    <ImageIcon size={14} />
                  </button>
                </Tooltip>
              );
            })()}
          </div>
        ),
      },
    ],
    [
      navigate,
      onAvailability,
      imagePresenceByPropertyId,
      imagePresenceLoading,
      imageUrlsByPropertyId,
      fallbackLogoUrlByPropertyId,
      returnView,
      onResynced,
      filter,
      onFilterChange,
    ],
  );

  return (
    <DataTable
      rows={rows}
      columns={columns}
      loading={loading}
      dense
      rowKey={(r) => r.id}
      onRowClick={onSelect}
      selectedKey={selectedKey}
      markVisitedRow
      sort={sort}
      onSortChange={onSortChange}
      selection={selection}
      customization={{
        tableId: "properties",
        lockedColumnIds: ["displayAddress", "apiDisplayAddress", "actions"],
        pinnedStartColumnIds: ["displayAddress", "apiDisplayAddress"],
        pinnedEndColumnIds: ["actions"],
      }}
      emptyTitle="No properties match"
      emptyDescription="Adjust price or bedroom filters to see more results."
    />
  );
}
